import { createMachine } from 'xstate';
import { pure } from 'xstate/lib/actions';

import shared from 'common/bento/shared';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import { AnyMachineEvent } from 'features/Bento/types/Abstract';

import actions, { Action } from './actions';
import guards, { Guard } from './guards';
import model, { Context } from './model';
import services from './services';

export enum State {
  NO_DOCUMENT_INIT = 'no-document-init',
  ADDRESS_LOAD = 'address-load',
  ADDRESS_FORM = 'address-form',
  ADDRESS_SEND = 'address-send',
  ACCOMMODATION_TYPE = 'accommodation-type',
  HOSTED_HOME = 'hosted-home',

  HOSTED_HOST_ID = 'hosted-host-id',
  HOSTED_HOST_ID_SEND = 'hosted-host-id-send',
  HOSTED_HOST_ID_DELETE = 'hosted-host-id-delete',

  HOSTED_ACCOMMODATION = 'hosted-accommodation',
  HOSTED_ACCOMMODATION_SEND = 'hosted-accommodation-send',
  HOSTED_ACCOMMODATION_DELETE = 'hosted-accommodation-delete',

  POA = 'poa',
  POA_TYPE = 'poa-type',
  POA_UPLOAD = 'poa-upload',
  POA_CHECKLIST = 'poa-checklist',
  POA_SEND = 'poa-send',
  POA_REVIEW = 'poa-review',
  POA_DELETE = 'poa-delete',

  HOSTED_POA = 'hosted-poa',
  HOSTED_POA_TYPE = 'hosted-poa-type',
  HOSTED_POA_UPLOAD = 'hosted-poa-upload',
  HOSTED_POA_CHECKLIST = 'hosted-poa-checklist',
  HOSTED_POA_SEND = 'hosted-poa-send',
  HOSTED_POA_REVIEW = 'hosted-poa-review',
  HOSTED_POA_DELETE = 'hosted-poa-delete',

  DONE = 'done',
  ABORT = 'abort',
}

const machine = createMachine<Context, AnyMachineEvent>(
  {
    context: model.initialContext,

    id: 'proof-of-address',

    initial: State.ADDRESS_LOAD,

    on: {
      '*': {
        actions: shared.actions.unhandled(),
      },
    },

    states: {
      [State.ADDRESS_LOAD]: {
        invoke: {
          id: 'loadInitialData',
          onDone: [
            {
              actions: Action.AssignInitialData,
              cond: Guard.HasNoKycAddressDocument,
              target: State.NO_DOCUMENT_INIT,
            },
            {
              actions: Action.AssignInitialData,
              cond: Guard.IsUserSelfHosted,
              target: State.POA,
            },
            {
              actions: Action.AssignInitialData,
              target: State.HOSTED_HOME,
            },
          ],
          onError: {
            actions: shared.actions.error(),
            target: State.NO_DOCUMENT_INIT,
          },
          src: 'loadInitialData',
        },
      },

      [State.NO_DOCUMENT_INIT]: {
        always: [
          {
            cond: Guard.IsAddressComplete,
            target: State.ACCOMMODATION_TYPE,
          },
          {
            target: State.ADDRESS_FORM,
          },
        ],
      },

      [State.ADDRESS_FORM]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: [
            {
              cond: Guard.HasNoKycAddressDocument,
              target: State.ACCOMMODATION_TYPE,
            },
            {
              cond: Guard.IsUserSelfHosted,
              target: State.POA,
            },
            {
              target: State.HOSTED_POA_REVIEW,
            },
          ],
          SUBMIT_ADDRESS: {
            actions: Action.AssignAddress,
            target: State.ADDRESS_SEND,
          },
        },
      },

      [State.ADDRESS_SEND]: {
        invoke: {
          onDone: {
            actions: Action.AssignAddress,
            target: State.ACCOMMODATION_TYPE,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.ADDRESS_FORM,
          },
          src: 'sendAddress',
        },
      },

      [State.ACCOMMODATION_TYPE]: {
        entry: shared.actions.trackView(),

        on: {
          EDIT_ADDRESS: State.ADDRESS_FORM,
          GO_HOSTED_HOME: State.HOSTED_HOME,
          GO_POA: State.POA,
          PREV: State.ABORT,
        },
      },

      [State.HOSTED_HOME]: {
        entry: shared.actions.trackView(),

        on: {
          GO_HOSTED_ACCOMMODATION: State.HOSTED_ACCOMMODATION,
          GO_HOSTED_HOST_ID: State.HOSTED_HOST_ID,
          GO_HOSTED_POA: State.HOSTED_POA,
          GO_HOSTED_POA_SELECT_TYPE: State.HOSTED_POA_TYPE,
          GO_POA: State.POA,
          NEXT: State.DONE,
          PREV: [
            {
              cond: Guard.IsHostedFlowComplete,
              target: State.DONE,
            },

            State.ACCOMMODATION_TYPE,
          ],
        },
      },

      [State.HOSTED_HOST_ID]: {
        entry: shared.actions.trackView(),

        on: {
          DELETE_DOCUMENT_FILE: State.HOSTED_HOST_ID_DELETE,
          NEXT: State.HOSTED_HOME,
          PREV: State.HOSTED_HOME,
          SUBMIT_DOCUMENT_FILE: {
            actions: Action.AssignDocumentFile,
            target: State.HOSTED_HOST_ID_SEND,
          },
        },
      },

      [State.HOSTED_HOST_ID_SEND]: {
        invoke: {
          id: 'createHostId',
          onDone: {
            target: State.HOSTED_HOST_ID,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.HOSTED_HOST_ID,
          },
          src: 'createKycDocument',
        },
      },

      [State.HOSTED_HOST_ID_DELETE]: {
        invoke: {
          id: 'deleteHostId',
          onDone: {
            target: State.HOSTED_HOST_ID,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.HOSTED_HOST_ID,
          },
          src: 'deleteKycDocument',
        },
      },

      [State.HOSTED_ACCOMMODATION]: {
        entry: shared.actions.trackView(),

        on: {
          DELETE_DOCUMENT_FILE: State.HOSTED_ACCOMMODATION_DELETE,
          NEXT: State.HOSTED_HOME,
          PREV: State.HOSTED_HOME,
          SUBMIT_DOCUMENT_FILE: {
            actions: 'assignDocumentFile',
            target: State.HOSTED_ACCOMMODATION_SEND,
          },
        },
      },

      [State.HOSTED_ACCOMMODATION_SEND]: {
        invoke: {
          id: 'createProofOfAccommodationId',
          onDone: {
            target: State.HOSTED_ACCOMMODATION,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.HOSTED_ACCOMMODATION,
          },
          src: 'createKycDocument',
        },
      },

      [State.HOSTED_ACCOMMODATION_DELETE]: {
        invoke: {
          id: 'deleteProofOfAccommodationId',
          onDone: {
            target: State.HOSTED_ACCOMMODATION,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.HOSTED_ACCOMMODATION,
          },
          src: 'deleteKycDocument',
        },
      },

      [State.POA]: {
        always: [
          {
            cond: Guard.IsProofOfAddressReviewable,
            target: State.POA_REVIEW,
          },
          {
            target: State.POA_TYPE,
          },
        ],
      },

      [State.POA_TYPE]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.ACCOMMODATION_TYPE,
          SUBMIT_POA_TYPE: {
            actions: [
              Action.AssignPoaType,

              pure((context, event) =>
                shared.actions.track({
                  name: 'POA_Select_Document_Type',
                  properties: {
                    type: event.data,
                  },
                }),
              ),
            ],
            target: State.POA_UPLOAD,
          },
        },
      },

      [State.POA_UPLOAD]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.POA_TYPE,
          SUBMIT_DOCUMENT_FILE: {
            actions: 'assignDocumentFile',
            target: State.POA_CHECKLIST,
          },
        },
      },

      [State.POA_CHECKLIST]: {
        entry: [
          shared.actions.trackView(),
          shared.actions.track({
            name: 'POA_User_Self_Verification_Started',
          }),
        ],
        on: {
          NEXT: State.POA_SEND,
          PREV: {
            actions: shared.actions.track({
              name: 'POA_User_Self_Verification_Failed',
            }),
            target: State.POA_UPLOAD,
          },
        },
      },

      [State.POA_SEND]: {
        invoke: {
          id: 'createProofOfAddress',
          onDone: {
            actions: shared.actions.track({
              name: 'POA_User_Self_Verification_Succeeded',
            }),
            target: State.POA_REVIEW,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.POA_CHECKLIST,
          },
          src: 'createKycDocument',
        },
      },

      [State.POA_DELETE]: {
        invoke: {
          id: 'deleteProofOfAddress',
          onDone: {
            target: State.POA_TYPE,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.POA_REVIEW,
          },
          src: 'deleteKycDocument',
        },
      },

      [State.POA_REVIEW]: {
        entry: shared.actions.trackView(),

        on: {
          DELETE_DOCUMENT_FILE: State.POA_DELETE,
          EDIT_ADDRESS: State.ADDRESS_FORM,
          NEXT: State.DONE,
          PREV: State.DONE,
        },
      },

      [State.HOSTED_POA]: {
        always: [
          {
            cond: Guard.IsHostedPoaStepDone,
            target: State.HOSTED_POA_REVIEW,
          },
          {
            target: State.HOSTED_POA_TYPE,
          },
        ],
      },

      [State.HOSTED_POA_TYPE]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.HOSTED_HOME,
          SUBMIT_POA_TYPE: {
            actions: 'assignPoaType',
            target: State.HOSTED_POA_UPLOAD,
          },
        },
      },

      [State.HOSTED_POA_UPLOAD]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.HOSTED_POA_TYPE,
          SUBMIT_DOCUMENT_FILE: {
            actions: 'assignDocumentFile',
            target: State.HOSTED_POA_CHECKLIST,
          },
        },
      },

      [State.HOSTED_POA_CHECKLIST]: {
        entry: [
          shared.actions.trackView(),
          shared.actions.track({
            name: 'POA_User_Self_Verification_Started',
          }),
        ],
        on: {
          NEXT: State.HOSTED_POA_SEND,
          PREV: {
            actions: shared.actions.track({
              name: 'POA_User_Self_Verification_Failed',
            }),
            target: State.HOSTED_POA_UPLOAD,
          },
        },
      },

      [State.HOSTED_POA_SEND]: {
        invoke: {
          id: 'createProofOfAddressHost',
          onDone: {
            actions: shared.actions.track({
              name: 'POA_User_Self_Verification_Succeeded',
            }),
            target: State.HOSTED_POA_REVIEW,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.HOSTED_POA,
          },
          src: 'createKycDocument',
        },
      },

      [State.HOSTED_POA_REVIEW]: {
        entry: shared.actions.trackView(),

        on: {
          DELETE_DOCUMENT_FILE: State.HOSTED_POA_DELETE,
          EDIT_ADDRESS: State.ADDRESS_FORM,
          NEXT: State.HOSTED_HOME,
          PREV: State.HOSTED_HOME,
        },
      },

      [State.HOSTED_POA_DELETE]: {
        invoke: {
          id: 'deleteProofOfAddressHost',
          onDone: {
            target: State.HOSTED_POA,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.HOSTED_POA,
          },
          src: 'deleteKycDocument',
        },
      },

      [State.DONE]: {
        data: {
          status: BentoModuleDoneStatus.DONE,
        },
        type: 'final',
      },

      [State.ABORT]: {
        data: {
          status: BentoModuleDoneStatus.ABORT,
        },
        type: 'final',
      },
    },
  },

  {
    actions,
    guards,
    services,
  },
);

export default machine;
